import {useState} from "react"
import {useNavigate} from "react-router-dom"
import {
    AppBar,
    Toolbar,
    Avatar,
    Typography,
    Box,
    Divider,
    IconButton,
    useMediaQuery,
    useTheme,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Drawer,
} from "@mui/material"
import MenuIcon from "@mui/icons-material/Menu"
import SettingsIcon from "@mui/icons-material/Settings"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import LogoutIcon from "@mui/icons-material/Logout"
// TODO(PO-543): Uncomment organization switching after backend integration is ready.
// import SwapHorizIcon from "@mui/icons-material/SwapHoriz"
import CloseIcon from "@mui/icons-material/Close"
import Cookies from "js-cookie"
import keycloak from "../keycloak"

// Import components
import Basket from "../components/basket-comp"
import Notification from "../notifications/notification-comp"
import Message from "../components/message-comp"
import UserDialog from "../dialog/user-dialog"
import AccountManagement from "../side-bar/account-management"
import LanguageSwitcher from "../components/language-switcher"
import SidebarMenu from "../menu/menu-bar"
import SettingsDialog from "../dialog/settings-dialog"
// import SwitchTenantDialog from "../dialog/switch-tenant-dialog"
import LogoHeader from "../assets/images/Logo-XMed-withText-white.png"
import OrderManagementHeader from "../assets/images/Order-Management.png"
import {APP_BAR_HEIGHT} from "../components/common/app-layout"

// Logo component for reusability
const Logo = ({onClick}) => (
    <Box
        component="img"
        src={LogoHeader}
        alt="XMed Order Management"
        onClick={onClick}
        sx={{
            height: {
                xs: "45px",
            },
            width: "auto",
            cursor: "pointer",
            objectFit: "contain",
            transition: "opacity 0.2s ease",
            "&:hover": {
                opacity: 0.8,
            },
        }}
    />
)

const OrderManagement = () => (
    <Box
        component="img"
        src={OrderManagementHeader}
        alt="XMed Order Management"
        sx={{
            height: {
                xs: "22px",
            },
            width: "auto",
            objectFit: "contain",
            mt: {sm: 1, md: 3},
        }}
    />
)

function AppBars({userFullName, tenantName, type, navOpen, toggleNav}) {
    const navigate = useNavigate()
    const theme = useTheme()

    // Responsive breakpoints
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))
    const isTablet = useMediaQuery("(max-width:960px)")

    // State
    const [modalOpen, setModalOpen] = useState(false)
    const [isSidebarOpen, setSidebarOpen] = useState(false)
    const [settingsOpen, setSettingOpen] = useState(false)
    // const [switchTenantOpen, setSwitchTenantOpen] = useState(false)
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [userMenuAnchor, setUserMenuAnchor] = useState(null)

    // Handlers
    const handleLogout = () => {
        setModalOpen(false)
        setUserMenuAnchor(null)
        Cookies.remove("accessToken")
        keycloak.logout({
            redirectUri: (window.location.href = "/"),
        })
    }

    const toggleModal = () => {
        setModalOpen(!modalOpen)
    }

    const handleSetting = () => {
        setModalOpen(false)
        setUserMenuAnchor(null)
        setSettingOpen(!settingsOpen)
    }

    // TODO(PO-543): Wire real tenant/session switching before enabling these controls.
    // const handleSwitchTenant = () => {
    //     setModalOpen(false)
    //     setUserMenuAnchor(null)
    //     setSwitchTenantOpen(!switchTenantOpen)
    // }

    const manageAccount = () => {
        setModalOpen(false)
        setUserMenuAnchor(null)
        setSidebarOpen(true)
    }

    const closeSidebar = () => {
        setSidebarOpen(false)
    }

    const handleBackToHome = () => {
        navigate("/")
        setMobileMenuOpen(false)
    }

    const handleUserMenuOpen = (event) => {
        setUserMenuAnchor(event.currentTarget)
    }

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null)
    }

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen)
    }

    // Mobile menu drawer content
    const mobileDrawerContent = (
        <Box sx={{width: 280, height: "100%", p: 0}}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    bgcolor: "#408ca8",
                }}
            >
                <Logo onClick={handleBackToHome} isMobile={true} isTablet={false}/>
                <IconButton onClick={toggleMobileMenu} sx={{color: "#fff"}}>
                    <CloseIcon/>
                </IconButton>
            </Box>

            <Box sx={{p: 2}}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                        p: 2,
                        bgcolor: "rgba(64, 140, 168, 0.1)",
                        borderRadius: 1,
                    }}
                >
                    <Avatar
                        sx={{bgcolor: "#408ca8"}}>{tenantName?.split(" ")[0]?.charAt(0)?.toUpperCase() || "U"}</Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            {tenantName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {userFullName}
                        </Typography>
                    </Box>
                </Box>

                <Box component="nav" sx={{mt: 2}}>
                    <MenuItem onClick={handleBackToHome} sx={{borderRadius: 1, mb: 1}}>
                        <ListItemText primary="Home"/>
                    </MenuItem>

                    {type === "PHARMACY" && (
                        <MenuItem sx={{borderRadius: 1, mb: 1}}>
                            <ListItemIcon>
                                <Basket/>
                            </ListItemIcon>
                            <ListItemText primary="Basket"/>
                        </MenuItem>
                    )}

                    <MenuItem sx={{borderRadius: 1, mb: 1}}>
                        <ListItemIcon>
                            <Notification tenantType={type}/>
                        </ListItemIcon>
                        <ListItemText primary="Notifications"/>
                    </MenuItem>

                    <MenuItem sx={{borderRadius: 1, mb: 1}}>
                        <ListItemIcon>
                            <Message/>
                        </ListItemIcon>
                        <ListItemText primary="Messages"/>
                    </MenuItem>

                    <Divider sx={{my: 2}}/>

                    <MenuItem onClick={manageAccount} sx={{borderRadius: 1, mb: 1}}>
                        <ListItemIcon>
                            <AccountCircleIcon/>
                        </ListItemIcon>
                        <ListItemText primary="Manage Account"/>
                    </MenuItem>

                    <MenuItem onClick={handleSetting} sx={{borderRadius: 1, mb: 1}}>
                        <ListItemIcon>
                            <SettingsIcon/>
                        </ListItemIcon>
                        <ListItemText primary="Settings"/>
                    </MenuItem>

                    {/* Organization switching awaits backend support.
                    <MenuItem onClick={handleSwitchTenant} sx={{borderRadius: 1, mb: 1}}>
                        <ListItemIcon>
                            <SwapHorizIcon/>
                        </ListItemIcon>
                        <ListItemText primary="Switch Tenant"/>
                    </MenuItem>
                    */}

                    <Box sx={{mt: 2, mb: 1}}>
                        <LanguageSwitcher/>
                    </Box>

                    <Divider sx={{my: 2}}/>

                    <MenuItem onClick={handleLogout} sx={{borderRadius: 1, color: "error.main"}}>
                        <ListItemIcon>
                            <LogoutIcon color="error"/>
                        </ListItemIcon>
                        <ListItemText primary="Logout"/>
                    </MenuItem>
                </Box>
            </Box>
        </Box>
    )

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: "#408ca8",
                    height: APP_BAR_HEIGHT,
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                    zIndex: theme.zIndex.drawer + 1,
                }}
            >
                <Toolbar
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        height: "100%",
                        px: {xs: 1, sm: 2},
                        minHeight: "unset !important", // Override default minHeight
                    }}
                >
                    {/* Left section - Logo and Title */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            flexGrow: 1,
                            overflow: "hidden",
                        }}
                    >
                        {/* Mobile menu button */}
                        {isMobile && (
                            <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleMobileMenu}
                                        sx={{mr: 1}}>
                                <MenuIcon/>
                            </IconButton>
                        )}

                        {/* Logo */}
                        {!isMobile && <Logo onClick={handleBackToHome} isMobile={isMobile} isTablet={isTablet}/>}
                        {!isMobile && (
                            <>
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                        height: {sm: "22px", md: "48px"},
                                        borderRightWidth: 2,
                                        mx: {sm: 1, md: 3},
                                    }}
                                />

                                <Box sx={{display: "flex", flexDirection: "column", justifyContent: "center"}}>
                                    <Typography
                                        variant="body2"

                                    >
                                        <OrderManagement/>
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Box>

                    {/* Right section - Actions */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: {xs: "8px", sm: "12px", md: "15px"},
                            ml: 2,
                        }}
                    >
                        {/* Language Switcher - Hidden on mobile */}
                        {!isMobile && (
                            <Box sx={{mr: {xs: 1, sm: 2, md: 3}}}>
                                <LanguageSwitcher/>
                            </Box>
                        )}

                        {/* Action Icons */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: {xs: "8px", sm: "12px", md: "15px"},
                                mr: {xs: 1, sm: 2, md: 3},
                            }}
                        >
                            {/* Show basket only for PHARMACY type */}
                            {type === "PHARMACY" && <Basket/>}

                            {/* Always show notifications and messages */}
                            <Notification tenantType={type}/>
                            <Message/>
                        </Box>

                        {/* User Avatar */}
                        <Avatar
                            alt={tenantName?.split(" ")[0] || "User"}
                            sx={{
                                cursor: "pointer",
                                background: "#96e3ff",
                                width: {xs: 32, sm: 36, md: 40},
                                height: {xs: 32, sm: 36, md: 40},
                            }}
                            onClick={isMobile ? handleUserMenuOpen : toggleModal}
                        >
                            {tenantName?.split(" ")[0]?.charAt(0)?.toUpperCase() || "U"}
                        </Avatar>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Mobile Menu */}
            <Drawer
                anchor="left"
                open={mobileMenuOpen}
                onClose={toggleMobileMenu}
                sx={{
                    "& .MuiDrawer-paper": {
                        width: "100%",
                        maxWidth: {xs: "100%", sm: 320},
                    },
                }}
            >
                {mobileDrawerContent}
            </Drawer>

            {/* Mobile User Menu */}
            <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
                transformOrigin={{horizontal: "right", vertical: "top"}}
                anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                PaperProps={{
                    elevation: 3,
                    sx: {mt: 1, width: 200},
                }}
            >
                <MenuItem onClick={manageAccount}>
                    <ListItemIcon>
                        <AccountCircleIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Manage Account</ListItemText>
                </MenuItem>

                <MenuItem onClick={handleSetting}>
                    <ListItemIcon>
                        <SettingsIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Settings</ListItemText>
                </MenuItem>

                {/* Organization switching awaits backend support.
                <MenuItem onClick={handleSwitchTenant}>
                    <ListItemIcon>
                        <SwapHorizIcon fontSize="small"/>
                    </ListItemIcon>
                    <ListItemText>Switch Tenant</ListItemText>
                </MenuItem>
                */}

                <Divider/>

                <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" color="error"/>
                    </ListItemIcon>
                    <ListItemText sx={{color: "error.main"}}>Logout</ListItemText>
                </MenuItem>
            </Menu>

            {/* Desktop Dialogs */}
            {/* TODO(PO-543): Restore this UserDialog prop after backend integration:
                onSwitchTenant={handleSwitchTenant}
            */}
            {modalOpen && (
                <UserDialog
                    open={modalOpen}
                    onClose={() => setModalOpen(false)}
                    onLogout={handleLogout}
                    onManageAccount={manageAccount}
                    tenantName={tenantName}
                    userEmail={userFullName}
                    onSettings={handleSetting}
                />
            )}

            <AccountManagement open={isSidebarOpen} onClose={closeSidebar} email={userFullName}
                               tenantName={tenantName}/>
            {!isMobile && <SidebarMenu tenantType={type} navOpen={navOpen} toggleNav={toggleNav}/>}

            {settingsOpen && <SettingsDialog open={settingsOpen} onClose={() => setSettingOpen(false)}/>}
            {/* Organization switching awaits backend support.
            {switchTenantOpen &&
                <SwitchTenantDialog open={switchTenantOpen} onClose={() => setSwitchTenantOpen(false)}/>}
            */}
        </>
    )
}

export default AppBars
